import { authRepository } from '@/domain/auth/repositories/authRepository';
import { cookieManager, SESSION_COOKIE_NAME } from '@/infrastructure/persistence/managers/CookieManager';
import { isDevelopment } from '@/infrastructure/persistence/managers/EnvManager';
import { localStorageManager } from '@/infrastructure/persistence/managers/LocalStorageManager';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';
import { authStore } from '@/infrastructure/store/authStore';

import { performTokenRefresh, setRefreshUserEmail } from './authRefresh';
import { setGatewayAccessToken, setGatewayRefreshTokenFn } from './gatewayFetch';
import { gatewayHttpClient } from './httpClient';
import { isPreviewSession, jwtExpiresInSeconds } from './previewSession';

type TokenPayload = {
  access_token: string;
  refresh_token: string;
};

let initializationPromise: Promise<void> | null = null;

const PREVIEW_HOST_SUFFIX = '.brainwave.umode.app';
let previewHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
let previewRouteTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Rota ao vivo para o painel do widget (iframe): avisa a página-mãe quando a
 * navegação muda. Tick barato de 1s, agnóstico ao router — além de manter a
 * rota visível no painel, a navegação conta como interação real e rearma o
 * decay de 2h do ambiente no control plane. Inerte fora do host de preview.
 */
function startPreviewRouteReporterIfPreviewHost(): void {
  if (previewRouteTimer !== null) {
    return;
  }
  if (!window.location.hostname.endsWith(PREVIEW_HOST_SUFFIX) || window.parent === window) {
    return;
  }
  let lastPath = '';
  const tick = (): void => {
    const path = `${window.location.pathname}${window.location.search}`;
    if (path !== lastPath) {
      lastPath = path;
      window.parent.postMessage({ type: 'megabrain-preview-route', path }, '*');
    }
  };
  tick();
  previewRouteTimer = setInterval(tick, 1_000);
}

/**
 * Keep-alive do preview do Brainwave: quando este app roda no host de preview
 * (aba externa, sem o widget polando ao lado), o reaper do control plane
 * mataria o ambiente da sessão em ~15min. O próprio app pinga o heartbeat
 * enquanto a aba viver. O flag `active` marca interação REAL do usuário desde
 * o último beat (clique/tecla) — só ela rearma o decay de 2h no servidor; uma
 * aba esquecida aberta não segura uma task paga pra sempre. Inerte em
 * qualquer outro host.
 */
function startPreviewHeartbeatIfPreviewHost(): void {
  if (previewHeartbeatTimer !== null) {
    return;
  }
  if (!window.location.hostname.endsWith(PREVIEW_HOST_SUFFIX)) {
    return;
  }
  let lastUserActivity = Date.now();
  const markActivity = (): void => {
    lastUserActivity = Date.now();
  };
  window.addEventListener('pointerdown', markActivity, { passive: true });
  window.addEventListener('keydown', markActivity, { passive: true });
  const sendHeartbeat = async (): Promise<void> => {
    try {
      await gatewayHttpClient.post('/services/megabrain/megabrain/preview/heartbeat', {
        body: {
          host: window.location.hostname,
          active: Date.now() - lastUserActivity < 70_000,
        },
      });
    } catch (error) {
      logger.debug('[SessionInitializer] preview heartbeat failed', error);
    }
  };
  sendHeartbeat();
  previewHeartbeatTimer = setInterval(sendHeartbeat, 60_000);
}

/**
 * URL do login unificado da casa, ou null quando o app nao foi configurado com
 * dashboard/appId. Exportada porque a tela de preview sem sessao oferece o
 * MESMO login como link, em vez de navegar sozinha.
 */
export function buildLoginUrl(): string | null {
  const dashboardURL = process.env.NEXT_PUBLIC_DASHBOARD_URL;
  const appId = process.env.NEXT_PUBLIC_APP_ID;

  if (!dashboardURL || !appId) {
    logger.error('[SessionInitializer] NEXT_PUBLIC_DASHBOARD_URL or NEXT_PUBLIC_APP_ID not found');
    return null;
  }

  const loginUrl = new URL(`${dashboardURL.replace(/\/$/, '')}/login`);
  loginUrl.searchParams.set('appId', appId);
  return loginUrl.toString();
}

function redirectToLogin(): void {
  // A preview session must never bounce to login — it mirrors the parent tab's
  // session (see performTokenRefresh). Render as-is instead of navigating away:
  // with no session at all the AuthGuard shows the sign-in screen for previews.
  if (isPreviewSession) {
    logger.debug('[SessionInitializer] preview session: skipping login redirect');
    return;
  }

  const url = buildLoginUrl();
  if (url === null) {
    return;
  }
  window.location.href = url;
}

function syncGateway(accessToken: string): void {
  setGatewayAccessToken(accessToken);
  setGatewayRefreshTokenFn(performTokenRefresh);
}

/**
 * Sessão entregue na URL. Além do `?token=` de sempre (o handoff do dashboard ao
 * abrir um app), aceita `#token=`: o fragmento NUNCA é enviado ao servidor, e é
 * por ele que o admin entrega a sessão a um preview sem o token passar por log
 * de gateway, de CDN ou do control plane. Em ambos os casos o valor é apagado da
 * URL logo depois, para não sobreviver num histórico ou num link copiado.
 */
function readTokensFromUrl(): TokenPayload | null {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const rawToken = search.get('token') ?? hash.get('token');
  if (!rawToken) {
    return null;
  }

  try {
    const decoded = JSON.parse(atob(rawToken)) as TokenPayload;

    localStorageManager.setItem('access_token', decoded.access_token);
    localStorageManager.setItem('refresh_token', decoded.refresh_token);

    search.delete('token');
    hash.delete('token');
    const qs = search.size > 0 ? `?${search.toString()}` : '';
    const rest = hash.size > 0 ? `#${hash.toString()}` : '';
    window.history.replaceState(null, '', `${window.location.pathname}${qs}${rest}`);

    return decoded;
  } catch (error) {
    logger.error('[SessionInitializer] Failed to decode token payload', error);
    return null;
  }
}

function readTokensFromSharedCookie(): TokenPayload | null {
  const cookieValue = cookieManager.get(SESSION_COOKIE_NAME);
  if (!cookieValue) return null;

  try {
    const decoded = JSON.parse(atob(cookieValue)) as TokenPayload;
    if (!decoded.access_token || !decoded.refresh_token) return null;
    // Token do cookie já vencido não serve para nada (o preview não renova por
    // conta própria): melhor cair no handoff da URL do que "logar" morto.
    const expiresIn = jwtExpiresInSeconds(decoded.access_token);
    if (expiresIn !== null && expiresIn <= 5) return null;

    localStorageManager.setItem('access_token', decoded.access_token);
    localStorageManager.setItem('refresh_token', decoded.refresh_token);

    return decoded;
  } catch (error) {
    logger.error('[SessionInitializer] Failed to decode shared cookie tokens', error);
    return null;
  }
}

async function loadUser(accessToken: string, refreshToken: string): Promise<void> {
  const { setTokens, setUser, setPartnersList } = authStore.getState();

  setTokens(accessToken, refreshToken);
  syncGateway(accessToken);

  try {
    const [userInfo, partnersList] = await Promise.all([authRepository.whoami(), authRepository.getPartnersList()]);

    if (!userInfo) {
      redirectToLogin();
      return;
    }

    if (userInfo.email) {
      setRefreshUserEmail(userInfo.email);
    }

    setUser(userInfo);
    setPartnersList(partnersList);
    startPreviewHeartbeatIfPreviewHost();
    startPreviewRouteReporterIfPreviewHost();

    if (isDevelopment) {
      const sessionPayload = btoa(JSON.stringify({ access_token: accessToken, refresh_token: refreshToken }));
      cookieManager.setShared(SESSION_COOKIE_NAME, sessionPayload);
    }
  } catch (error) {
    logger.error('[SessionInitializer] Failed to load user', error);
    redirectToLogin();
  }
}

export function initializeSession(): Promise<void> {
  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    try {
      // Sempre consome (e limpa) o token da URL, mesmo que ele não seja usado:
      // ninguém quer um token sobrando no histórico do navegador.
      const tokensFromUrl = readTokensFromUrl();

      // Em PREVIEW, a sessão da casa vem antes do token entregue na URL. Quem
      // abre um preview pelo admin manda a sessão DO ADMIN no fragmento, e ela
      // é outra pessoa para a lista de acesso do projeto: se o navegador já tem
      // a sessão compartilhada (o login normal, em `.umode.app`), é ela que vale.
      // O handoff continua sendo a saída quando não existe sessão nenhuma.
      const sharedFirst = isPreviewSession ? readTokensFromSharedCookie() : null;
      if (sharedFirst) {
        await loadUser(sharedFirst.access_token, sharedFirst.refresh_token);
        return;
      }

      if (tokensFromUrl) {
        await loadUser(tokensFromUrl.access_token, tokensFromUrl.refresh_token);
        return;
      }

      const accessToken = localStorageManager.getItem<string>('access_token');
      const refreshToken = localStorageManager.getItem<string>('refresh_token');
      if (accessToken && refreshToken) {
        if (!cookieManager.get(SESSION_COOKIE_NAME)) {
          redirectToLogin();
          return;
        }
        await loadUser(accessToken, refreshToken);
        return;
      }

      const tokensFromCookie = readTokensFromSharedCookie();
      if (tokensFromCookie) {
        await loadUser(tokensFromCookie.access_token, tokensFromCookie.refresh_token);
        return;
      }

      redirectToLogin();
    } finally {
      authStore.getState().setInitialized(true);
    }
  })();

  return initializationPromise;
}
